<div class="access-control-container">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="loading-state">
        <div class="spinner"></div>
        <p>Verifying permissions...</p>
    </div>

    <div *ngIf="!isLoading" class="fade-in">
        <header class="dashboard-header">
            <h1>Access Control</h1>
            <div class="user-info">
                <span class="role-label">Current Role:</span>
                <span class="role-badge" [ngClass]="userRole || 'guest'" [ngStyle]="{'background-color': getRoleColor(userRole || 'guest', (roles$ | async))}">{{ userRole || 'Guest' }}</span>
            </div>
        </header>

        <!-- Tabs -->
        <div class="tabs-container">
            <button class="tab-btn" [class.active]="activeTab === 'page-access'" (click)="switchTab('page-access')">Page Access</button>
            <button class="tab-btn" [class.active]="activeTab === 'users'" (click)="switchTab('users')">User Management</button>
            <button class="tab-btn" [class.active]="activeTab === 'roles'" (click)="switchTab('roles')">Role Management</button>
        </div>

        <!-- TAB 1: PAGE ACCESS (Dynamic) -->
        <div *ngIf="activeTab === 'page-access'" class="tab-content fade-in">
            
            <!-- ADMIN VIEW: Configuration Matrix -->
            <div *ngIf="userRole === 'admin' || userRole === 'superadmin'; else userView" class="management-panel">
                <h3>Page Access Configuration</h3>
                <p class="matrix-subtitle">Select which roles can access specific pages.</p>
                
                <ng-container *ngIf="{ roles: roles$ | async, perms: pagePermissions$ | async } as data">
                    <table class="data-table matrix-table" *ngIf="data.roles && data.perms">
                        <thead>
                            <tr>
                                <th>Page Name</th>
                                <th *ngFor="let role of data.roles" class="text-center">{{ role.name }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <ng-container *ngFor="let page of availablePages">
                                <tr [class.expandable-row]="true">
                                    <td>
                                        <div class="page-name-cell">
                                            <button type="button" 
                                                    class="expand-toggle-btn" 
                                                    [class.expanded]="expandedPages.has(page.id)"
                                                    [class.no-children]="!hasChildSettings(page.id)"
                                                    (click)="togglePageExpand(page.id)"
                                                    [title]="hasChildSettings(page.id) ? 'Toggle sub-options' : 'No sub-options available'">
                                                <svg class="chevron-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                    <polyline points="9 18 15 12 9 6"></polyline>
                                                </svg>
                                            </button>
                                            <span class="icon">{{ page.icon }}</span> 
                                            <span>{{ page.name }}</span>
                                        </div>
                                    </td>
                                    <td *ngFor="let role of data.roles" class="text-center">
                                        <input type="checkbox" 
                                               [checked]="isRoleAllowed(page.id, role.name, data.perms)"
                                               [disabled]="isPageCheckboxDisabled(page.id, role)"
                                               (change)="togglePagePermission(page.id, page.name, role.name, $event, data.perms)">
                                    </td>
                                </tr>

                                <!-- Sub-option: Show all companies -->
                                <tr *ngIf="pagesWithCompanyFilterSubOption.has(page.id) && expandedPages.has(page.id)" class="sub-permission-row">
                                    <td>
                                        <div class="sub-option-label">
                                            <span class="sub-indent-arrow">↳</span>
                                            <span class="sub-option-text">Show all companies</span>
                                        </div>
                                    </td>
                                    <td *ngFor="let role of data.roles" class="text-center">
                                        <input type="checkbox" 
                                               [checked]="role.name.toLowerCase() === 'superadmin' || isShowAllCompaniesAllowed(page.id, role.name, data.perms)"
                                               [disabled]="role.name.toLowerCase() === 'superadmin' || !isRoleAllowed(page.id, role.name, data.perms) || isPageCheckboxDisabled(page.id, role) || role.isExternal"
                                               (change)="toggleShowAllCompanies(page.id, page.name, role.name, $event, data.perms)">
                                    </td>
                                </tr>

                                <!-- Sub-options: Specific Page Actions (e.g. Restart All Machines, Release App) -->
                                <ng-container *ngIf="pageActionSubOptions[page.id] && expandedPages.has(page.id)">
                                    <tr *ngFor="let subOpt of pageActionSubOptions[page.id]" class="sub-permission-row">
                                        <td>
                                            <div class="sub-option-label">
                                                <span class="sub-indent-arrow">↳</span>
                                                <span *ngIf="subOpt.icon" class="sub-icon">{{ subOpt.icon }}</span>
                                                <span class="sub-option-text">{{ subOpt.name }}</span>
                                            </div>
                                        </td>
                                        <td *ngFor="let role of data.roles" class="text-center">
                                            <input type="checkbox" 
                                                   [checked]="isRoleAllowed(subOpt.id, role.name, data.perms)"
                                                   [disabled]="isPageCheckboxDisabled(page.id, role) || !isRoleAllowed(page.id, role.name, data.perms)"
                                                   (change)="togglePagePermission(subOpt.id, subOpt.name, role.name, $event, data.perms)">
                                        </td>
                                    </tr>
                                </ng-container>

                                <!-- Sub-options: Chat Agents (Dynamically listed from Agent Management) -->
                                <ng-container *ngIf="page.id === 'chat-agent' && expandedPages.has(page.id)">
                                    <ng-container *ngIf="(chatAgents$ | async) as chatAgents">
                                        <tr *ngFor="let agent of chatAgents" class="sub-permission-row">
                                            <td>
                                                <div class="sub-option-label">
                                                    <span class="sub-indent-arrow">↳</span>
                                                    <span class="sub-icon">{{ agent.icon }}</span>
                                                    <span class="sub-option-text">{{ agent.name }}</span>
                                                    <span *ngIf="agent.badge" class="agent-sub-badge">{{ agent.badge }}</span>
                                                </div>
                                            </td>
                                            <td *ngFor="let role of data.roles" class="text-center">
                                                <input type="checkbox" 
                                                       [checked]="isAgentSubOptionAllowed(agent.subOptionId, role.name, data.perms)"
                                                       [disabled]="isPageCheckboxDisabled(page.id, role) || !isRoleAllowed(page.id, role.name, data.perms)"
                                                       (change)="togglePagePermission(agent.subOptionId, agent.name, role.name, $event, data.perms)">
                                            </td>
                                        </tr>
                                    </ng-container>
                                </ng-container>

                                <!-- Fallback info sub-row for pages without additional sub-options -->
                                <tr *ngIf="!hasChildSettings(page.id) && expandedPages.has(page.id)" class="sub-permission-row empty-sub-row">
                                    <td [attr.colspan]="data.roles.length + 1">
                                        <div class="sub-option-label empty-sub-label">
                                            <span class="sub-indent-arrow">↳</span>
                                            <span class="sub-option-text muted-text">No additional sub-settings for this page.</span>
                                        </div>
                                    </td>
                                </tr>
                            </ng-container>
                        </tbody>
                    </table>
                </ng-container>
            </div>

            <!-- NON-ADMIN VIEW: My Accessible Pages -->
            <ng-template #userView>
                <div class="card-grid">
                    <ng-container *ngIf="pagePermissions$ | async as perms">
                        <ng-container *ngFor="let page of availablePages">
                            <div *ngIf="isRoleAllowed(page.id, userRole || '', perms)" class="card">
                                <div class="card-header">
                                    <h3>{{ page.name }}</h3>
                                    <span class="icon">{{ page.icon }}</span>
                                </div>
                                <div class="card-body">
                                    <p>Access granted.</p>
                                    <div class="button-group">
                                        <button *ngIf="page.id !== 'chat-agent'" class="btn primary" [routerLink]="['/home', page.id]">Go to {{ page.name }}</button>
                                        <button *ngIf="page.id === 'chat-agent'" class="btn primary" (click)="openChatAgent()">Open {{ page.name }}</button>
                                    </div>
                                </div>
                            </div>
                        </ng-container>
                    </ng-container>

                    <div *ngIf="!userRole" class="access-denied">
                        <div class="alert-box">
                            <h3>Access Denied</h3>
                            <p>You do not have permission to view this page.</p>
                        </div>
                    </div>
                </div>
            </ng-template>
        </div>

        <!-- TAB 2: USER MANAGEMENT -->
        <div *ngIf="activeTab === 'users'" class="tab-content fade-in">
            <div class="management-panel">
                <h3>User Management</h3>

                <!-- User Overview KPI Cards -->
                <div class="user-stats-grid">
                    <div class="user-stat-card">
                        <div class="user-stat-header">
                            <span class="user-stat-label">Total Registered Accounts</span>
                            <span class="user-stat-icon">👥</span>
                        </div>
                        <div class="user-stat-value">
                            {{ isLoadingCounts ? '...' : (userCounts.total | number) }}
                        </div>
                        <div class="user-stat-subtext">All active ecosystem accounts</div>
                    </div>

                    <div class="user-stat-card">
                        <div class="user-stat-header">
                            <span class="user-stat-label">Staff & Operational Users</span>
                            <span class="user-stat-icon">🛡️</span>
                        </div>
                        <div class="user-stat-value">
                            {{ isLoadingCounts ? '...' : (userCounts.staff | number) }}
                        </div>
                        <div class="user-stat-subtext">Admins, managers & accountants</div>
                    </div>

                    <div class="user-stat-card">
                        <div class="user-stat-header">
                            <span class="user-stat-label">Customer Portal Users</span>
                            <span class="user-stat-icon">📸</span>
                        </div>
                        <div class="user-stat-value">
                            {{ isLoadingCounts ? '...' : (userCounts.customers | number) }}
                        </div>
                        <div class="user-stat-subtext">Photobooth guests & media gallery</div>
                    </div>
                </div>
                
                <div class="table-actions">
                    <div class="search-box-container">
                        <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        <input 
                            type="text" 
                            [(ngModel)]="searchEmail" 
                            (ngModelChange)="onSearchChange()" 
                            placeholder="Search by email or name..." 
                            class="user-search-input" 
                        />
                        <button *ngIf="searchEmail" (click)="clearSearch()" class="clear-search-btn" title="Clear search">&times;</button>
                    </div>

                    <div class="filter-dropdown" [class.dropdown-active]="showRoleFilterDropdown">
                      <button class="action-btn utility-btn dropdown-trigger-btn" (click)="toggleRoleFilterDropdown()" [class.active]="showRoleFilterDropdown">
                        <span>Select Roles ({{ selectedRoles.size }}/{{ filterRolesList.length }})</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" [class.rotated]="showRoleFilterDropdown"><polyline points="6 9 12 15 18 9"></polyline></svg>
                      </button>
                      <div class="dropdown-menu" *ngIf="showRoleFilterDropdown">
                        <div class="dropdown-item" (click)="toggleAllRolesFilter()">
                          <input type="checkbox" [checked]="isAllRolesSelected" (click)="$event.stopPropagation(); toggleAllRolesFilter()">
                          <span>Select All</span>
                        </div>
                        <div class="dropdown-divider" *ngIf="filterRolesList.length > 0"></div>
                        <div class="dropdown-scroll">
                          <div class="dropdown-item" *ngFor="let r of filterRolesList" (click)="toggleRoleFilterItem(r.name)">
                            <input type="checkbox" [checked]="isRoleFilterSelected(r.name)" (click)="$event.stopPropagation(); toggleRoleFilterItem(r.name)">
                            <span>{{ r.name }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button (click)="openAddUserModal()" class="btn primary">Add User</button>
                </div>

                <!-- Users Table -->
                <ng-container *ngIf="{ users: users$ | async, owners: owners$ | async, roles: roles$ | async } as data">
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Assigned Owners</th>
                                <th>Registered Date</th>
                                <th>Last Login Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let user of getPaginatedUsers(data.users || [], data.roles || [])">
                                <td>
                                    {{ user.displayName }}
                                </td>
                                <td>{{ user.email }}</td>
                                <td>
                                    <span class="role-badge" [ngClass]="getRoleName(user, data.roles).toLowerCase()" [ngStyle]="{'background-color': getRoleColorForUser(user, data.roles)}">{{ getRoleName(user, data.roles) }}</span>
                                </td>
                                <td>
                                    {{ getDisplayOwners(user.ownerIds, data.owners) }}
                                </td>
                                <td>
                                    {{ formatDate(user.registeredAt) }}
                                </td>
                                <td>
                                    {{ formatDate(user.lastLoginAt || user.lastLogin) }}
                                </td>
                                <td>
                                    <button (click)="openEditUserModal(user)" class="btn-icon" title="Edit">✏️</button>
                                    <button (click)="deleteUser(user.uid)" class="btn-icon delete">🗑️</button>
                                </td>
                            </tr>
                            <tr *ngIf="getPaginatedUsers(data.users || [], data.roles || []).length === 0">
                                <td colspan="7" class="no-data-cell">
                                    No users found matching your search criteria.
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Pagination Controls -->
                    <div class="pagination-controls">
                        <div class="page-size-selector">
                            <label>Rows per page:</label>
                            <select [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange()">
                                <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
                            </select>
                        </div>
                        <div class="page-navigation">
                            <span>Page {{ currentPage }} of {{ totalPages }}</span>
                            <div class="page-btn-group">
                                <button (click)="prevPage()" [disabled]="currentPage === 1" class="btn utility-btn page-nav-btn">Previous</button>
                                <button (click)="nextPage()" [disabled]="currentPage === totalPages" class="btn utility-btn page-nav-btn">Next</button>
                            </div>
                        </div>
                    </div>
                </ng-container>

                <!-- Edit User Modal -->
                <div *ngIf="editingUser" class="modal-overlay">
                    <div class="modal-content">
                        <h3>Edit User</h3>
                        <div class="form-group">
                            <label>Email</label>
                            <input type="text" [value]="editingUser.email" disabled class="form-input full-width">
                        </div>
                        <div class="form-group">
                            <label>Display Name</label>
                            <input type="text" [(ngModel)]="editingUser.displayName" class="form-input full-width">
                        </div>
                        <div class="form-group">
                            <label>Role</label>
                            <select [(ngModel)]="editingUser.role" class="form-select full-width">
                                <option *ngIf="editingUser.role?.toLowerCase() === 'superadmin'" [value]="editingUser.role" disabled>{{ editingUser.role }} (Protected)</option>
                                <option *ngFor="let r of assignableRoles" [value]="r.name">{{ r.name }}</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Linked Owners</label>
                            <div class="checkbox-list">
                                <div *ngFor="let o of owners$ | async" class="checkbox-item">
                                    <label>
                                        <input type="checkbox" [checked]="isOwnerSelected(editingUser, o.id)" (change)="toggleOwnerSelection(editingUser, o.id, $event)">
                                        {{ o.name }}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="modal-actions">
                            <button (click)="closeEditUserModal()" class="btn secondary">Cancel</button>
                            <button (click)="saveUser()" class="btn primary">Save Changes</button>
                        </div>
                    </div>
                </div>

                <!-- Add User Modal -->
                <div *ngIf="isAddingUser" class="modal-overlay">
                    <div class="modal-content">
                        <h3>Add User</h3>
                        <div *ngIf="addUserErrorMessage" class="modal-error-alert">
                            {{ addUserErrorMessage }}
                        </div>
                        <div class="form-group">
                            <label>Email</label>
                            <input type="email" [(ngModel)]="newUser.email" (ngModelChange)="addUserErrorMessage = null" class="form-input full-width" placeholder="e.g. user@example.com">
                        </div>
                        <div class="form-group">
                            <label>Display Name</label>
                            <input type="text" [(ngModel)]="newUser.displayName" class="form-input full-width" placeholder="e.g. John Doe">
                        </div>
                        <div class="form-group">
                            <label>Role</label>
                            <select [(ngModel)]="newUser.role" class="form-select full-width">
                                <option *ngFor="let r of assignableRoles" [value]="r.name">{{ r.name }}</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Linked Owners</label>
                            <div class="checkbox-list">
                                <div *ngFor="let o of owners$ | async" class="checkbox-item">
                                    <label>
                                        <input type="checkbox" [checked]="isOwnerSelected(newUser, o.id)" (change)="toggleOwnerSelection(newUser, o.id, $event)">
                                        {{ o.name }}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="modal-actions">
                            <button (click)="closeAddUserModal()" class="btn secondary" [disabled]="isSavingUser">Cancel</button>
                            <button (click)="addUser()" class="btn primary" [disabled]="isSavingUser">{{ isSavingUser ? 'Adding...' : 'Add User' }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- TAB 3: ROLE MANAGEMENT -->
        <div *ngIf="activeTab === 'roles'" class="tab-content fade-in">
            <div class="management-panel">
                <div class="panel-header-with-actions">
                    <div>
                        <h3>Role Management</h3>
                        <p class="matrix-subtitle">Define system roles and assign special privilege boundaries.</p>
                    </div>
                    <button (click)="openAddRoleModal()" class="btn primary">+ Add Role</button>
                </div>

                <!-- Roles List -->
                <div class="roles-list">
                    <div *ngFor="let role of assignableRoles" class="role-item">
                        <div class="role-info">
                            <span class="role-badge" [ngClass]="role.name" [ngStyle]="{'background-color': $any(role).color || getRoleColor(role.name, null)}">{{ role.name }}</span>
                            <span *ngIf="role.isCustomer" class="customer-role-pill">Customer Role</span>
                            <span *ngIf="!role.isCustomer && role.isExternal" class="external-role-pill">External Role</span>
                            <span class="role-desc">{{ role.description }}</span>
                        </div>
                        <div class="role-actions">
                            <button (click)="openEditRoleModal(role)" class="btn-icon edit" title="Edit Role">✏️</button>
                            <button (click)="deleteRole(role.id)" class="btn-icon delete" title="Delete Role">🗑️</button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add Role Modal -->
            <div *ngIf="isAddingRole" class="modal-overlay" (click)="closeAddRoleModal()">
                <div class="modal-content role-modal-card" (click)="$event.stopPropagation()">
                    <div class="modal-header">
                        <h3 class="modal-title">Add Role</h3>
                        <button type="button" class="modal-close-btn" (click)="closeAddRoleModal()" title="Close">&times;</button>
                    </div>

                    <div *ngIf="roleErrorMessage" class="modal-error-alert">
                        {{ roleErrorMessage }}
                    </div>

                    <div class="modal-body">
                        <div class="form-group">
                            <label class="form-label">Role Name <span class="required-asterisk">*</span></label>
                            <input type="text" [(ngModel)]="newRole.name" (ngModelChange)="roleErrorMessage = null" class="form-input full-width" placeholder="e.g. Regional Partner">
                        </div>

                        <div class="form-group">
                            <label class="form-label">Description</label>
                            <input type="text" [(ngModel)]="newRole.description" class="form-input full-width" placeholder="Brief description of duties...">
                        </div>

                        <div class="form-group">
                            <label class="form-label">Badge Color</label>
                            <div class="color-picker-row">
                                <input type="color" [(ngModel)]="newRole.color" class="color-input">
                                <span class="color-preview-badge" [ngStyle]="{'background-color': newRole.color || '#3b82f6'}">{{ newRole.name || 'Preview' }}</span>
                            </div>
                        </div>

                        <!-- Special Role Options Cards -->
                        <div class="role-type-cards">
                            <!-- External Role Card -->
                            <div class="role-option-card" [class.active-card]="newRole.isExternal && !newRole.isCustomer" [class.disabled-card]="newRole.isCustomer">
                                <label class="role-option-label">
                                    <input type="checkbox" [(ngModel)]="newRole.isExternal" [disabled]="!!newRole.isCustomer" class="role-checkbox">
                                    <div class="role-option-content">
                                        <div class="role-option-title">
                                            <span>External Role</span>
                                            <span class="role-chip chip-external">Restricted</span>
                                        </div>
                                        <p class="role-option-desc">Restricts page access strictly to <strong>Profit Report</strong> and <strong>Daily Report</strong>, and limits the brand dropdown to owner companies.</p>
                                    </div>
                                </label>
                            </div>

                            <!-- Customer Role Card -->
                            <div class="role-option-card" [class.active-card]="newRole.isCustomer">
                                <label class="role-option-label">
                                    <input type="checkbox" [(ngModel)]="newRole.isCustomer" (ngModelChange)="newRole.isCustomer ? newRole.isExternal = false : null" class="role-checkbox">
                                    <div class="role-option-content">
                                        <div class="role-option-title">
                                            <span>Customer Role</span>
                                            <span class="role-chip chip-customer">No ERP Access</span>
                                        </div>
                                        <p class="role-option-desc">Customer portal guest account. <strong>Disables and unchecks all page permissions.</strong> Accounts with this role will never have access to the ERP system.</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button (click)="closeAddRoleModal()" class="btn secondary" [disabled]="isSavingRole">Cancel</button>
                        <button (click)="addRole()" class="btn primary" [disabled]="isSavingRole">{{ isSavingRole ? 'Saving...' : 'Create Role' }}</button>
                    </div>
                </div>
            </div>

            <!-- Edit Role Modal -->
            <div *ngIf="isEditingRole && editingRole" class="modal-overlay" (click)="closeEditRoleModal()">
                <div class="modal-content role-modal-card" (click)="$event.stopPropagation()">
                    <div class="modal-header">
                        <h3 class="modal-title">Edit Role</h3>
                        <button type="button" class="modal-close-btn" (click)="closeEditRoleModal()" title="Close">&times;</button>
                    </div>

                    <div *ngIf="roleErrorMessage" class="modal-error-alert">
                        {{ roleErrorMessage }}
                    </div>

                    <div class="modal-body">
                        <div class="form-group">
                            <label class="form-label">Role Name <span class="required-asterisk">*</span></label>
                            <input type="text" [(ngModel)]="editingRole.name" (ngModelChange)="roleErrorMessage = null" class="form-input full-width">
                        </div>

                        <div class="form-group">
                            <label class="form-label">Description</label>
                            <input type="text" [(ngModel)]="editingRole.description" class="form-input full-width" placeholder="Brief description of duties...">
                        </div>

                        <div class="form-group">
                            <label class="form-label">Badge Color</label>
                            <div class="color-picker-row">
                                <input type="color" [(ngModel)]="editingRole.color" class="color-input">
                                <span class="color-preview-badge" [ngStyle]="{'background-color': editingRole.color || '#3b82f6'}">{{ editingRole.name || 'Preview' }}</span>
                            </div>
                        </div>

                        <!-- Special Role Options Cards -->
                        <div class="role-type-cards">
                            <!-- External Role Card -->
                            <div class="role-option-card" [class.active-card]="editingRole.isExternal && !editingRole.isCustomer" [class.disabled-card]="editingRole.isCustomer">
                                <label class="role-option-label">
                                    <input type="checkbox" [(ngModel)]="editingRole.isExternal" [disabled]="!!editingRole.isCustomer" class="role-checkbox">
                                    <div class="role-option-content">
                                        <div class="role-option-title">
                                            <span>External Role</span>
                                            <span class="role-chip chip-external">Restricted</span>
                                        </div>
                                        <p class="role-option-desc">Restricts page access strictly to <strong>Profit Report</strong> and <strong>Daily Report</strong>, and limits the brand dropdown to owner companies.</p>
                                    </div>
                                </label>
                            </div>

                            <!-- Customer Role Card -->
                            <div class="role-option-card" [class.active-card]="editingRole.isCustomer">
                                <label class="role-option-label">
                                    <input type="checkbox" [(ngModel)]="editingRole.isCustomer" (ngModelChange)="editingRole.isCustomer ? editingRole.isExternal = false : null" class="role-checkbox">
                                    <div class="role-option-content">
                                        <div class="role-option-title">
                                            <span>Customer Role</span>
                                            <span class="role-chip chip-customer">No ERP Access</span>
                                        </div>
                                        <p class="role-option-desc">Customer portal guest account. <strong>Disables and unchecks all page permissions.</strong> Accounts with this role will never have access to the ERP system.</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button (click)="closeEditRoleModal()" class="btn secondary" [disabled]="isSavingRole">Cancel</button>
                        <button (click)="saveRole()" class="btn primary" [disabled]="isSavingRole">{{ isSavingRole ? 'Saving...' : 'Save Changes' }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
